import React from 'react'
import { updateRoute } from '../route'
import { FormattedMessage } from 'react-intl'
import { VulnerabilityPolicy, VulnerabilityPolicyAction } from '.'
import {
  ControlsNoData,
  ControlsWithData,
  LegacyGrid,
  SortConfig
} from '../../legacy-design-system/legacy-grid'
import { LegacyEmptyState } from '../../legacy-design-system/legacy-empty-state'
import {
  ActionsCell,
  ApplicationsCell,
  DescriptionCell,
  ToggleCell,
  EnvironmentsCell,
  PolicyCell,
  VulnerabilityRulesCell
} from './cell-content'
import { hasRole, Role } from '../roles'

const routeToAddPolicyPage = (
  organizationID: string,
  action: VulnerabilityPolicyAction
) => {
  let actionType = 'auto-verification'
  if ('NOTIFY' === action) {
    actionType = 'notify'
  }

  updateRoute(
    `/${organizationID}/rules/vulnerability-management/policy/add/${actionType}`
  )
}

const routeToEditPolicy = (organizationID: string, policyId: string) =>
  updateRoute(
    `/${organizationID}/rules/vulnerability-management/policy/edit/${policyId}`
  )

export interface VulnerabilityPoliciesGridManagerViewProps {
  readonly loading: boolean
  readonly refetch: () => Promise<any>
  readonly organizationID: string
  readonly organizationRole: Role
  readonly vulnerabilityPolicies: VulnerabilityPolicy[]
  readonly action: VulnerabilityPolicyAction
  readonly onSort: (sortConfig: SortConfig<VulnerabilityPolicy>) => void
  readonly onSearch: (value: string) => void
  readonly noData: boolean
  readonly activeSortConfigs: Array<SortConfig<VulnerabilityPolicy>>
}

export function VulnerabilityPoliciesGridManagerView({
  loading,
  refetch,
  organizationID,
  organizationRole,
  vulnerabilityPolicies,
  action,
  onSort,
  onSearch,
  noData,
  activeSortConfigs
}: VulnerabilityPoliciesGridManagerViewProps) {
  const addPolicyProps = {
    addBtnTitle: {
      id: 'action_remediationPolicy_addPolicy'
    },
    addBtnDataE2e: 'route-to-add-auto-verification-policy-form',
    onClickAdd: () => routeToAddPolicyPage(organizationID, action)
  }

  return (
    <React.Fragment>
      {noData === true ? (
        <ControlsNoData {...addPolicyProps} />
      ) : (
        <ControlsWithData
          {...addPolicyProps}
          onSearch={onSearch}
          searchPlaceholder={{
            id: 'placeholder_remediationPolicy_findPolicy'
          }}
        />
      )}
      {noData === true && (
        <LegacyEmptyState
          children={
            <FormattedMessage id="detail_vulnManagement_noVulnPolicies" />
          }
        />
      )}
      {noData === false && (
        <LegacyGrid<VulnerabilityPolicy>
          data-testid="auto-verification-policies-manager-grid"
          data-e2e="auto-verification-policies-manager-grid"
          onSort={onSort}
          loading={loading}
          rowData={vulnerabilityPolicies}
          emptyGridMessageConfig={{
            id: 'detail_vulnManagement_noVulnPoliciesFound'
          }}
          columnConfigs={[
            {
              styles: {
                maxWidth: '230px',
                width: '230px'
              },
              heading: {
                id: 'title_policy'
              },
              sorting: {
                field: 'name'
              },
              CellContent: ({ rowData }) => (
                <PolicyCell
                  organizationID={organizationID}
                  rowData={rowData}
                  routeToEditPolicy={routeToEditPolicy}
                />
              )
            },
            {
              styles: {
                maxWidth: '240px',
                width: '240px'
              },
              heading: {
                id: 'title_vulnerabilityRules'
              },
              CellContent: ({ rowData }) => (
                <VulnerabilityRulesCell
                  rowData={rowData}
                  listDataTestID="auto-verification-vulnerability-rules-cell"
                />
              )
            },
            {
              styles: {
                maxWidth: '240px',
                width: '240px'
              },
              heading: {
                id: 'title_applications'
              },
              CellContent: ({ rowData }) => (
                <ApplicationsCell
                  organizationID={organizationID}
                  rowData={rowData}
                  listDataTestID="auto-verification-applications-cell"
                />
              )
            },
            {
              styles: {
                maxWidth: '200px',
                width: '200px'
              },
              heading: {
                id: 'title_description'
              },
              CellContent: ({ rowData }) => (
                <DescriptionCell
                  rowData={rowData}
                  dataE2e={'auto-verification-description-cell'}
                />
              )
            },
            {
              styles: {
                maxWidth: '200px',
                width: '200px'
              },
              heading: {
                id: 'title_environments'
              },
              CellContent: ({ rowData }) => (
                <EnvironmentsCell
                  rowData={rowData}
                  listDataTestID="auto-verification-environments-cell"
                />
              )
            },
            {
              styles: {
                maxWidth: '90px',
                width: '90px'
              },
              horizontalCenter: true,
              heading: {
                id: 'title_rules_enabled_policy'
              },
              CellContent: ({ rowData }) => (
                <ToggleCell
                  organizationID={organizationID}
                  rowData={{
                    ...rowData,
                    disabled: !hasRole(organizationRole, 'ADMIN')
                  }}
                  dataE2e={'enabled-toggle'}
                />
              )
            },
            {
              styles: {
                maxWidth: '40px',
                width: '40px',
                padding: '10px 10px 10px 12px'
              },
              horizontalCenter: true,
              CellContent: ({ rowData }) => (
                <ActionsCell
                  organizationID={organizationID}
                  organizationRole={organizationRole}
                  refetch={refetch}
                  rowData={rowData}
                  dataE2e={'delete-button'}
                />
              )
            }
          ]}
          activeSortConfigs={activeSortConfigs}
        />
      )}
    </React.Fragment>
  )
}
